import { API } from 'src/services'
import { formatarDataParaEnvio } from 'src/utils/formatadoras/formatadoras'


const URL_CORRECAO_AUTOMATICA = 'relatorio/gerar-correcao-automatica-de-marcacoes'
const INTERVALO_POLL_MS = 5000
const TIPO_RELATORIO_CORRECAO = 'Correção automática de marcações'

// Relatório multi-empregado (assíncrono) espera o texto da ação.
const TIPO_CORRECAO_POR_ACAO = {
  correct: 'corrigir',
  undo: 'desfazer'
}

// Rotas FastAPI síncronas, uma por ação.
const ROTA_FASTAPI_POR_ACAO = {
  correct: API.correcaoAutomaticaCorrigir,
  undo: API.correcaoAutomaticaDesfazer
}

const esperar = (ms) => new Promise(resolve => setTimeout(resolve, ms))

function obterEmpregadosDistintos (lines) {
  return [...new Set(lines.map(linha => linha.employee.id))]
}

function montarPayloadCorrecaoDeUmEmpregado ({ employeeId, startDate, endDate }) {
  return {
    id_empregado: employeeId,
    data_inicial: formatarDataParaEnvio(startDate),
    data_final: formatarDataParaEnvio(endDate)
  }
}

function montarPayloadCorrecaoMultipla ({ ids, action, startDate, endDate }) {
  return {
    entidade: 'employee',
    ids,
    opcoes_do_relatorio: {
      data_inicial: formatarDataParaEnvio(startDate),
      data_final: formatarDataParaEnvio(endDate),
      tipo_correcao: TIPO_CORRECAO_POR_ACAO[action],
      considerar_demitidos: false
    }
  }
}

/**
 * Corrige (ou desfaz) automaticamente as marcações de um único empregado via
 * rota FastAPI síncrona (registros/correcoes/{corrigir,desfazer}).
 *
 * Usada tanto pela folha individual (sempre 1 empregado) quanto pelo caso
 * single de enviarCorrecaoAutomatica (verificar folhas).
 *
 * @param {Object} params
 * @param {string} params.employeeId id do empregado.
 * @param {'correct'|'undo'} params.action ação de correção.
 * @param {Date|import('moment').Moment} params.startDate início do período.
 * @param {Date|import('moment').Moment} params.endDate fim do período.
 *
 *
 * @returns {Promise<object>} resposta HTTP; body no contrato { sucesso, mensagem }.
 */
export async function corrigirMarcacoesDeUmEmpregado ({ employeeId, action, startDate, endDate }) {
  const rota = ROTA_FASTAPI_POR_ACAO[action]
  const payload = montarPayloadCorrecaoDeUmEmpregado({ employeeId, startDate, endDate })
  return rota.save(payload)
}

/**
 * Dispara a correção automática de marcações escolhendo a rota conforme a quantidade
 * de empregados distintos nas linhas carregadas.
 *
 * - 1 empregado  -> FastAPI síncrono
 * - >1 empregado -> relatório assíncrono
 *
 * @param {Object} params
 * @param {Array<{id: string, employee: {id: string}}>} params.lines linhas da folha já carregadas.
 * @param {'correct'|'undo'} params.action ação de correção.
 * @param {Date|import('moment').Moment} params.startDate início do período.
 * @param {Date|import('moment').Moment} params.endDate fim do período.
 *
 *
 * @returns {Promise<{
 *   multiplo: boolean,
 *   resposta: object,
 *   taskId: string|null,
 *   relatorioInfo: {taskId: string, url: string, tipo: string}|null
 * }>}
 */
export async function enviarCorrecaoAutomatica ({ lines, action, startDate, endDate }) {
  const ids = obterEmpregadosDistintos(lines)

  if (ids.length <= 1) {
    const resposta = await corrigirMarcacoesDeUmEmpregado({ employeeId: ids[0], action, startDate, endDate })
    return { multiplo: false, resposta, taskId: null, relatorioInfo: null }
  }

  const payload = montarPayloadCorrecaoMultipla({ ids, action, startDate, endDate })
  const resposta = await API.correcaoAutomaticaDeMarcacoes.save(payload)
  const taskId = resposta.body.task_id

  return {
    multiplo: true,
    resposta,
    taskId,
    relatorioInfo: { taskId, url: URL_CORRECAO_AUTOMATICA, tipo: TIPO_RELATORIO_CORRECAO }
  }
}

/**
 * Faz poll do status da correção até o backend devolver `relatorio_id` (relatório concluído),
 * permitindo que o caller dê o mesmo refresh do caso single após a conclusão.
 *
 *
 * @param {string} taskId id retornado por enviarCorrecaoAutomatica no caso multi.
 * @returns {Promise<string>} relatorio_id ao concluir.
 */
export async function aguardarConclusaoCorrecao (taskId) {
  const { body } = await API.correcaoAutomaticaDeMarcacoes.get({ task_id: taskId })
  if (body.relatorio_id) {
    return body.relatorio_id
  }
  await esperar(INTERVALO_POLL_MS)
  return aguardarConclusaoCorrecao(taskId)
}
